import styles from "./RecurringEventSelector.module.scss";
import { RenderWhen } from "../../../../../common/infrastructure/components/RenderWhen";
import { getRecurringPeriodType } from "../../helpers/getRecurringPeriodType";
import { DefaultInputField } from "../../../../../common/components/forms/inputs/InputField/DefaultInputField";
import { DefaultFormSelectField } from "../../../../../common/components/forms/inputs/dropdown/DefaultFormSelectField";
import { IEmployeeWithAvailabilityModel } from "../../../../../common/components/forms/inputs/instructorCalendar/models/employeeCalendarModels";
import { useRecurringEventSelector } from "./hooks/useRecurringEventSelector";
import { IRecurringConflictItem } from "./models/recurringEventModels";
import { DefaultButton } from "../../../../../common/components/button/defaultButton/DefaultButton";
import { DefaultToggle } from "../../../../../common/components/forms/inputs/toggle/DefaultToggle";
import { formatBufferAfter } from "../../../../../common/infrastructure/utilities/durationUtility";

import { dvSize } from "../../../../../common/infrastructure/utilities/viewportUnitsUtility";
interface IRecurringEventSelectorProps {
  pageMode?: string;
  isRecurringFieldName: string;
  recurringDaysFieldName: string;
  recurringPeriodFieldName: string;
  recurringPeriodTypeFieldName: string;
  recurringIntervalFieldName: string;
  recurringIntervalTypeFieldName: string;
  checkboxLabel: string;
  /** Replaces the employee-bookings check; it may ignore `dates` and build its own. */
  onAvailabilityCheck?: (dates: Date[]) => Promise<IRecurringConflictItem[]>;
  onRecurringStatusChange?: (
    isRecurring: boolean,
    hasChecked: boolean,
    isFormValid: boolean,
    hasConflicts: boolean,
  ) => void;
  /** The event's name, when the custom availability check depends on it. */
  nameFieldName?: string;
  dateFieldName?: string;
  durationFieldName?: string;
  /** A service's buffer after the reservation; counted as busy time too. */
  bufferAfterMinutes?: number;
  employeeIdFieldName?: string;
  studioEmployees?: IEmployeeWithAvailabilityModel[];
  width?: string;
}

export const RecurringEventSelector = ({
  pageMode,
  isRecurringFieldName,
  recurringDaysFieldName,
  recurringPeriodFieldName,
  recurringPeriodTypeFieldName,
  recurringIntervalFieldName,
  recurringIntervalTypeFieldName,
  checkboxLabel,
  onAvailabilityCheck,
  onRecurringStatusChange,
  nameFieldName,
  dateFieldName,
  durationFieldName,
  bufferAfterMinutes,
  employeeIdFieldName,
  studioEmployees,
  width,
}: IRecurringEventSelectorProps) => {
  const {
    isRecurring,
    isRecurringDisabled,
    recurringDisabledTooltip,
    selectedDays,
    isChecking,
    conflicts,
    hasChecked,
    validationErrors,
    isFormValid,
    weekdays,
    isDesktopOrTablet,
    toggleDay,
    checkAvailability,
    getPreviewText,
  } = useRecurringEventSelector({
    isRecurringFieldName,
    recurringDaysFieldName,
    recurringPeriodFieldName,
    recurringPeriodTypeFieldName,
    recurringIntervalFieldName,
    recurringIntervalTypeFieldName,
    nameFieldName,
    dateFieldName,
    durationFieldName,
    bufferAfterMinutes,
    employeeIdFieldName,
    studioEmployees,
    onAvailabilityCheck,
    onRecurringStatusChange,
    pageMode,
  });

  const recurringPeriodTypeOptions = getRecurringPeriodType();

  if (!width) {
    if (isDesktopOrTablet) {
      width = "600px";
    } else {
      width = dvSize("80dvw");
    }
  }

  if (pageMode === "edit") {
    return null;
  }

  return (
    <div style={{ width }}>
      <DefaultToggle
        name={isRecurringFieldName}
        label={checkboxLabel}
        maxWidth={width}
        disabled={isRecurringDisabled}
        tooltip={recurringDisabledTooltip}
      />

      <RenderWhen condition={!!isRecurring}>
        <div className={styles.container}>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Изберете дни</h3>
            <div className={styles.weekdaysGrid}>
              {weekdays.map((day) => (
                <button
                  key={day.id}
                  type="button"
                  onClick={() => toggleDay(day.id)}
                  className={`${styles.dayButton} ${
                    selectedDays?.includes(day.id)
                      ? styles.dayButtonSelected
                      : ""
                  }`}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          {/* Interval */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>На какъв интервал?</h3>
            <div className={styles.formRow}>
              <RenderWhen condition={isDesktopOrTablet}>
                <span className={styles.label}>На</span>
              </RenderWhen>

              <div className={styles.duration}>
                <DefaultInputField
                  name={recurringIntervalFieldName}
                  placeholder={"30"}
                  className={styles.duration_input}
                  type="number"
                  width={"13%"}
                  minWidth="50px"
                />
                <DefaultFormSelectField
                  className={styles.duration_dropdown}
                  name={recurringIntervalTypeFieldName}
                  options={recurringPeriodTypeOptions}
                  width="30%"
                  minWidth="141px"
                />
              </div>
            </div>
          </div>

          {/* Duration */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>За колко време?</h3>

            <div className={styles.formRow}>
              <RenderWhen condition={isDesktopOrTablet}>
                <span className={styles.label}>За</span>
              </RenderWhen>

              <div className={styles.duration}>
                <DefaultInputField
                  name={recurringPeriodFieldName}
                  placeholder={"30"}
                  className={styles.duration_input}
                  type="number"
                  width={"13%"}
                  minWidth="50px"
                />
                <DefaultFormSelectField
                  className={styles.duration_dropdown}
                  name={recurringPeriodTypeFieldName}
                  options={recurringPeriodTypeOptions}
                  width="30%"
                  minWidth="141px"
                />
              </div>
            </div>
          </div>

          {/* Validation Errors */}
          <RenderWhen condition={validationErrors.length > 0}>
            <div className={styles.validationErrors}>
              {validationErrors.map((error, index) => (
                <div key={index} className={styles.validationError}>
                  {error}
                </div>
              ))}
            </div>
          </RenderWhen>

          {/* Preview */}
          <RenderWhen condition={selectedDays?.length > 0 && isFormValid}>
            <div className={styles.preview}>
              <p className={styles.previewText}>
                <strong>График:</strong> {getPreviewText()}
              </p>
            </div>
          </RenderWhen>

          {/* Check Availability Button */}
          <DefaultButton
            label={"Проверка за конфликти"}
            onClick={checkAvailability}
            disabled={false}
            loading={isChecking}
            className={styles.checkButton}
          />

          {/* Loading */}
          <RenderWhen condition={isChecking}>
            <div className={styles.loading}>
              <p className={styles.loadingText}>Календарът се сканира...</p>
            </div>
          </RenderWhen>

          {/* Conflicts */}
          <RenderWhen condition={conflicts.length > 0 && !isChecking}>
            <div className={styles.conflicts}>
              <h4 className={styles.conflictsTitle}>
                ⚠️ Конфликти в разписанието
              </h4>
              {conflicts.map((conflict, index) => (
                <div key={index} className={styles.conflictItem}>
                  <div className={styles.conflictDate}>
                    {conflict.dayName}, {conflict.date}
                  </div>
                  <div className={styles.conflictEvent}>
                    Конфликт с: {conflict.title} ({conflict.startTime} -{" "}
                    {conflict.endTime}
                    {conflict.bufferAfterMinutes
                      ? ` ${formatBufferAfter(conflict.bufferAfterMinutes)} буфер`
                      : ""}
                    )
                  </div>
                </div>
              ))}
            </div>
          </RenderWhen>

          {/* No Conflicts */}
          <RenderWhen
            condition={conflicts.length === 0 && !isChecking && hasChecked}
          >
            <div className={styles.success}>
              <p className={styles.successText}>
                ✅ Не бяха открити конфликти.
              </p>
            </div>
          </RenderWhen>
        </div>
      </RenderWhen>
    </div>
  );
};
